import React, { useEffect, useMemo, useState } from "react";

const BASE = `${process.env.PUBLIC_URL}/images`;

const IMAGES = {
  datanex: `${BASE}/datanex.png`,
  eduboxx: `${BASE}/eduboxx.png`,
  eduinsights: `${BASE}/eduinsights.png`,
  eduflux: `${BASE}/eduflux.png`,
  eduaikit: `${BASE}/eduaikit.png`,
  kroton: `${BASE}/kroton.jpg`,
  anhanguera: `${BASE}/anhanguera.jpg`,
  lfg: `${BASE}/lfg.jpg`,
  damasio: `${BASE}/damasio.jpg`,
  hero: `${BASE}/Hero-datanexai.jpg`,
  ecosystem: `${BASE}/Ecosystem-datanexai.jpg`,
  architecture: `${BASE}/Architecture-datanexai.jpg`,
  dashboard: `${BASE}/Dashboard-datanexai.jpg`,
  ailab: `${BASE}/Ai-Lab-datanexai.jpg`,
  eduboxxHero: `${BASE}/Eduboxx-datanexai.jpg`,
  printedM: `${BASE}/PrintedM-datanexai.jpg`,
  government: `${BASE}/Government-datanexai.jpg`,
};

/* =========================
   DATANEX BRAND PALETTE
   (matches the existing light blue / cyan Datanex theme)
========================= */
const BRAND = {
  bg: "#f5fafe",
  white: "#ffffff",
  text: "#0f172a",
  muted: "rgba(15,23,42,0.72)",
  subtle: "rgba(15,23,42,0.55)",
  line: "rgba(15,23,42,0.10)",
  card: "rgba(255,255,255,0.92)",
  cardStrong: "rgba(255,255,255,0.96)",
  blue: "#1d4ed8",
  blueDark: "#1e3a8a",
  cyan: "#0891b2",
  cyanLight: "#22d3ee",
  sky: "#3b82f6",
  ink: "#0369a1",
  chipBg: "rgba(37, 99, 235, 0.10)",
  chipBorder: "rgba(37, 99, 235, 0.20)",
  softTile: "#eff6ff",
  shadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
  shadowStrong: "0 28px 70px rgba(15, 23, 42, 0.12)",
};

const CONTENT = {
  en: {
    nav: {
      speed: "Full Speed",
      proof: "Proof",
      problem: "Problem",
      solution: "Solution",
      products: "Platform",
      flow: "Flow",
      impact: "Impact",
      cases: "Case Studies",
      licensing: "Brazil Licensing",
      contact: "Contact",
    },
    tagline: "Learning Intelligence. Built on pedagogy. Powered by AI.",
    hero: {
      eyebrow: "DatanexAI • Brazil Master Licensing Agent",
      title:
        "Eduboxx Turns Educational Technology Into Something Governments Can Actually See, Trust, and Deploy",
      subtitle:
        "Eduboxx is the physical deployment of a complete education technology system for Brazil — combining visible infrastructure with personalized learning generated from your teachers' lesson plans, admissions evaluation and placement, and simulation-based challenges, all aligned to the BNCC and built on real pedagogy.",
      primaryCta: "Request a Pilot",
      secondaryCta: "Explore Brazil Rollout",
      stats: [
        "Proven in Brazil",
        "Millions of streams delivered",
        "Thousands of students served",
        "Built for public-sector scale",
      ],
      sideTitle: "Why this message wins",
      sideBullets: [
        "It leads with something tangible and physically understandable.",
        "It transforms a complex platform into one clear deployment story.",
        "It proves the system has already worked at scale in Brazil.",
        "It creates urgency without sounding abstract or experimental.",
      ],
    },
    speed: {
      title: "Every student, full speed — without overloading your internet.",
      subtitle:
        "Eduboxx delivers EduFlux's rich, personalized learning to an entire classroom at once — from a local node inside the school, so 30 students streaming lessons and simulations don't bring the connection to its knees.",
      problem:
        "A school can have internet and still stall. One shared connection wasn't built for a whole classroom pulling video, simulations, and personalized content at the same moment. The more engaging the content, the worse the bottleneck. 'Connected' doesn't mean 'ready for 30 students at once.'",
      features: [
        "Concurrent delivery at classroom scale: Eduboxx serves content from a local node over the school's own network, so every student gets full-speed, full-quality learning simultaneously — no buffering, no queue.",
        "Your connection stays free: heavy content is delivered locally; the internet link only carries lightweight sync. Other classes, admin systems, and the school's bandwidth aren't starved.",
        "Built for rich content: video lessons, EduAIKit simulations, Teli's voice — the heavy, engaging media that cloud-only delivery struggles to push to many students at once.",
        "Works when connectivity is limited too: in schools with slow, shared, or unreliable internet, Eduboxx keeps learning running at full quality regardless.",
        "Syncs when it can: progress and signals flow back to the learner record when the connection allows, keeping the record complete.",
      ],
      differentiation:
        "Cloud-only platforms slow down exactly when a classroom needs them most — when everyone's learning at once. Eduboxx delivers to every student at full speed, and leaves your internet connection free to do everything else.",
      cta: "Bring full-speed learning to every classroom →",
    },
    proof: {
      title: "Proven at Scale in Brazil",
      intro:
        "Eduboxx is not a concept. It has already been successfully implemented in major Brazilian education environments, delivering large-scale educational streaming and classroom access in real operational settings.",
      stats: [
        "Millions of streams delivered",
        "Thousands of students reached",
        "Multi-site deployment experience",
        "Validated in real education environments",
      ],
      institutionsTitle: "Implemented in",
      description:
        "These deployments proved that Eduboxx could deliver reliable educational content at scale across complex learning environments — creating the foundation for the more powerful integrated DatanexAI model now being positioned for Brazil.",
    },
    problem: {
      title: "The Problem",
      intro:
        "Education systems want modernization, visibility, and better learning outcomes — but most initiatives fail long before they create real transformation.",
      cards: [
        {
          title: "Infrastructure Reality",
          text: "Many schools and networks still operate in environments where bandwidth, reliability, and simultaneous access remain major constraints.",
        },
        {
          title: "Fragmented Systems",
          text: "Content delivery, analytics, operations, and learning support are often spread across disconnected tools that create friction and confusion.",
        },
        {
          title: "Weak Procurement Narratives",
          text: "Public-sector buyers often struggle to trust or evaluate solutions that are presented only as software, dashboards, or cloud abstractions.",
        },
        {
          title: "Pilot-to-Scale Failure",
          text: "Even promising pilots often collapse because the model was never designed to become a scalable operating system.",
        },
      ],
    },
    solution: {
      title: "Why Eduboxx Wins",
      intro:
        "Eduboxx wins because it starts where trust starts: with something visible, physical, and deployable — then delivers the full platform inside that deployment.",
      cards: [
        {
          title: "Visible Infrastructure",
          text: "Eduboxx gives governments and institutions something tangible to evaluate, deploy, and expand.",
        },
        {
          title: "Integrated by Design",
          text: "EduInsights for admissions evaluation and placement, EduFlux for personalized learning, and EduAIKit for simulation-based challenges — integrated capabilities inside the Eduboxx system, sharing one learner record.",
        },
        {
          title: "Operationally Clear",
          text: "One platform. One deployment path. One story from pilot to scale.",
        },
        {
          title: "Built for Expansion",
          text: "The model is designed to move from real implementations into regional, state, and national rollout opportunities.",
        },
      ],
    },
    products: {
      title: "Inside the Eduboxx Platform",
      intro:
        "Eduboxx is the visible deployment layer. Inside it sits a complete education technology ecosystem — built on pedagogy, powered by AI, and aligned to the BNCC.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Physical Infrastructure",
          logoKey: "eduboxx",
          features: [
            "On-site deployment layer for schools and networks",
            "Visible, tangible infrastructure for public-sector confidence",
            "Supports large-scale content delivery in constrained environments",
            "Creates the physical gateway for full platform adoption",
          ],
        },
        {
          name: "EduFlux",
          tag: "Personalized Learning Intelligence",
          logoKey: "eduflux",
          features: [
            "Generative personalization: reads each teacher's lesson plan and creates the lesson, homework, and practice for every student — not pulled from a fixed bank",
            "Aligned to the BNCC: every generated lesson, task, and assessment maps to BNCC habilidades — with per-habilidade reporting so teachers and coordinators see coverage and mastery, not just activity",
            "The Teli tutor inside the work: an AI tutor that guides students through tasks in real time, with voice and text",
            "Develops Power Skills and Core Attributes — the transferable competencies behind frameworks like the IB Approaches to Learning",
          ],
        },
        {
          name: "EduInsights",
          tag: "Admissions Evaluation & Placement",
          logoKey: "eduinsights",
          features: [
            "Signal detection beyond a single test score — surfaces how an applicant thinks, learns, and grows",
            "Fairer, fuller evaluation that compares applicants on more than memorized content",
            "Informs placement and day-one support, not just admit or deny",
            "Feeds the same learner record that follows the student into the classroom",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Simulation-Based Learning · Part of EduFlux",
          logoKey: "eduaikit",
          features: [
            "Simulation-based challenges: students reason through scenarios and make decisions, not just recall facts",
            "Strong in non-STEM too — history, social studies, ethics, the arts — where fixed question banks fall short",
            "Generated from the lesson itself, personalized to each student",
            "Develops reasoning, perspective-taking, evidence use, and communication",
          ],
        },
      ],
      closing:
        "Together, these components make Eduboxx far more than a delivery box. They make it the physical expression of a complete education operating system — generative, pedagogy-led, BNCC-aligned, and built on one learner record that follows each student across every surface.",
    },
    flow: {
      title: "How the System Works",
      intro:
        "The Eduboxx model is designed to move from visible deployment to measurable educational and operational impact.",
      steps: [
        { title: "1. Deploy", text: "Install Eduboxx in pilot schools, campuses, classrooms, or target regions." },
        { title: "2. Deliver", text: "Provide reliable access to educational content and services in real school environments." },
        { title: "3. Evaluate & Place", text: "Use EduInsights to evaluate applicants beyond a single test score and inform placement and day-one support." },
        { title: "4. Personalize Learning", text: "Use EduFlux to generate personalized lessons from each teacher's plan — BNCC-aligned, with the Teli tutor guiding students through the work." },
        { title: "5. Build Reasoning", text: "Use EduAIKit to turn lessons into simulation-based challenges across every subject, including non-STEM." },
        { title: "6. Scale", text: "Expand from pilot implementation into network-wide, municipal, state, or national rollouts." },
      ],
    },
    impact: {
      title: "Results and Impact",
      intro:
        "Eduboxx creates value at multiple levels: operationally, administratively, strategically, and educationally.",
      cards: [
        { title: "More Reliable Delivery", text: "Stabilize access to educational content across real-world school environments." },
        { title: "Personalized Learning at Scale", text: "Each student receives instruction generated from your teachers' lesson plans, aligned to the BNCC, with the Teli tutor guiding them through the work." },
        { title: "Richer Admissions Signal", text: "Admissions teams get fuller signal than a single test score, and what's learned about each applicant informs placement and day-one support." },
        { title: "Deeper Reasoning, Every Subject", text: "Simulation-based challenges develop transferable competencies — including in non-STEM subjects where fixed question banks fall short." },
      ],
    },
    cases: {
      title: "Before and After",
      intro:
        "These scenarios show what changes when Eduboxx replaces fragmented educational delivery with an integrated deployment model.",
      beforeLabel: "Before",
      afterLabel: "After",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before: "Institutions needed reliable large-scale educational video delivery across multiple environments, but infrastructure limitations made consistent performance difficult.",
          after: "Eduboxx supported large-scale streaming delivery, helping serve thousands of students and enabling millions of educational streams in real learning environments.",
        },
        {
          title: "Anhanguera / Kroton",
          before: "Large educational networks needed a scalable way to deliver content across multiple sites with varying operational realities.",
          after: "Eduboxx created a stable and scalable infrastructure model capable of supporting simultaneous access across distributed educational environments.",
        },
        {
          title: "From Fragmentation to System",
          before: "Delivery, operations, and visibility were disconnected, making scale difficult and procurement harder to justify.",
          after: "Eduboxx created one understandable deployment story — visible infrastructure outside, integrated intelligence and learning power inside.",
        },
      ],
    },
    licensing: {
      title: "Brazil Licensing Structure",
      body:
        "DatanexAI acts as the master licensing agent for Brazil, leading with Eduboxx as the visible and physically demonstrable education system while delivering the integrated value of EduInsights, EduFlux, and EduAIKit as part of the same platform.",
      bullets: [
        "One system, one procurement story",
        "Physical deployment with integrated digital intelligence",
        "Proven foundation for Brazil market entry",
        "Scalable from pilot to large public-sector rollout",
      ],
    },
    contact: {
      title: "Let's Build the Right Brazil Deployment",
      intro:
        "Start a conversation around pilots, regional implementations, licensing, or large-scale public-sector education deployment in Brazil.",
      form: {
        name: "Name",
        org: "Organization",
        email: "Email",
        region: "Country / Region",
        interest: "Primary Interest",
        message: "Message",
        options: [
          "Eduboxx Pilot",
          "Government Deployment",
          "Regional Rollout",
          "Licensing Partnership",
        ],
        submit: "Submit Inquiry",
        select: "Select...",
      },
      note: "This front-end form is ready to connect to your CRM, backend endpoint, or WordPress handler.",
    },
    footer: {
      left: "DatanexAI",
      right: "Brazil master licensing agent for the Eduboxx integrated education system.",
    },
  },

  pt: {
    nav: {
      speed: "Velocidade Total",
      proof: "Provas",
      problem: "Problema",
      solution: "Solução",
      products: "Plataforma",
      flow: "Fluxo",
      impact: "Impacto",
      cases: "Casos",
      licensing: "Licenciamento Brasil",
      contact: "Contato",
    },
    tagline: "Inteligência de Aprendizagem. Construída sobre pedagogia. Movida por IA.",
    hero: {
      eyebrow: "DatanexAI • Agente Master de Licenciamento no Brasil",
      title:
        "Eduboxx Transforma Tecnologia Educacional em Algo que Governos Podem Ver, Confiar e Implantar",
      subtitle:
        "Eduboxx é a implantação física de um sistema completo de tecnologia educacional para o Brasil — combinando infraestrutura visível com aprendizagem personalizada gerada a partir dos planos de aula dos seus professores, avaliação e classificação de admissões e desafios baseados em simulação, todos alinhados à BNCC e construídos sobre pedagogia real.",
      primaryCta: "Solicitar um Piloto",
      secondaryCta: "Explorar Expansão no Brasil",
      stats: [
        "Comprovado no Brasil",
        "Milhões de streams entregues",
        "Milhares de alunos atendidos",
        "Construído para escala pública",
      ],
      sideTitle: "Por que esta mensagem funciona",
      sideBullets: [
        "Começa com algo tangível e fisicamente compreensível.",
        "Transforma uma plataforma complexa em uma história clara de implantação.",
        "Prova que o sistema já funcionou em escala no Brasil.",
        "Cria urgência sem parecer abstrato ou experimental.",
      ],
    },
    speed: {
      title: "Cada aluno em velocidade máxima — sem sobrecarregar sua internet.",
      subtitle:
        "O Eduboxx entrega a aprendizagem rica e personalizada do EduFlux para uma sala de aula inteira ao mesmo tempo — a partir de um nó local dentro da escola, para que 30 alunos transmitindo aulas e simulações não levem a conexão ao limite.",
      problem:
        "Uma escola pode ter internet e ainda assim travar. Uma conexão compartilhada não foi feita para uma turma inteira puxando vídeo, simulações e conteúdo personalizado no mesmo instante. Quanto mais envolvente o conteúdo, pior o gargalo. 'Conectado' não significa 'pronto para 30 alunos ao mesmo tempo.'",
      features: [
        "Entrega simultânea em escala de sala de aula: o Eduboxx serve o conteúdo a partir de um nó local pela rede da própria escola, para que cada aluno receba aprendizagem em velocidade e qualidade máximas ao mesmo tempo — sem buffering, sem fila.",
        "Sua conexão fica livre: o conteúdo pesado é entregue localmente; o link da internet só carrega sincronização leve. Outras turmas, sistemas administrativos e a banda da escola não ficam sufocados.",
        "Feito para conteúdo rico: aulas em vídeo, simulações do EduAIKit, a voz do Teli — a mídia pesada e envolvente que a entrega só pela nuvem tem dificuldade em servir para muitos alunos ao mesmo tempo.",
        "Funciona também onde a conectividade é limitada: em escolas com internet lenta, compartilhada ou instável, o Eduboxx mantém a aprendizagem rodando em qualidade máxima.",
        "Sincroniza quando dá: progresso e sinais voltam ao registro de aprendizagem quando a conexão permite, mantendo o registro completo.",
      ],
      differentiation:
        "Plataformas que dependem só da nuvem ficam lentas exatamente quando a sala de aula mais precisa — quando todo mundo está aprendendo ao mesmo tempo. O Eduboxx entrega para cada aluno em velocidade máxima e deixa sua conexão com a internet livre para tudo o mais.",
      cta: "Leve aprendizagem em velocidade máxima para cada sala de aula →",
    },
    proof: {
      title: "Comprovado em Escala no Brasil",
      intro:
        "Eduboxx não é um conceito. Já foi implementado com sucesso em grandes ambientes educacionais brasileiros, entregando streaming educacional em larga escala e acesso em sala de aula em configurações operacionais reais.",
      stats: [
        "Milhões de streams entregues",
        "Milhares de alunos alcançados",
        "Experiência de implantação multi-site",
        "Validado em ambientes educacionais reais",
      ],
      institutionsTitle: "Implementado em",
      description:
        "Essas implantações provaram que o Eduboxx pode entregar conteúdo educacional confiável em escala em ambientes de aprendizagem complexos — criando a base para o modelo integrado DatanexAI, agora sendo posicionado para o Brasil.",
    },
    problem: {
      title: "O Problema",
      intro:
        "Sistemas educacionais querem modernização, visibilidade e melhores resultados de aprendizagem — mas a maioria das iniciativas falha muito antes de criar uma transformação real.",
      cards: [
        { title: "Realidade da Infraestrutura", text: "Muitas escolas e redes ainda operam em ambientes onde largura de banda, confiabilidade e acesso simultâneo permanecem como grandes limitações." },
        { title: "Sistemas Fragmentados", text: "Entrega de conteúdo, analytics, operações e suporte ao aprendizado frequentemente estão espalhados em ferramentas desconectadas que criam atrito e confusão." },
        { title: "Narrativas Fracas de Aquisição", text: "Compradores do setor público frequentemente têm dificuldade em confiar ou avaliar soluções apresentadas apenas como software, dashboards ou abstrações na nuvem." },
        { title: "Falha do Piloto à Escala", text: "Mesmo pilotos promissores frequentemente colapsam porque o modelo nunca foi projetado para se tornar um sistema operacional escalável." },
      ],
    },
    solution: {
      title: "Por Que o Eduboxx Vence",
      intro:
        "Eduboxx vence porque começa onde a confiança começa: com algo visível, físico e implantável — e então entrega a plataforma completa dentro dessa implantação.",
      cards: [
        { title: "Infraestrutura Visível", text: "Eduboxx dá a governos e instituições algo tangível para avaliar, implantar e expandir." },
        { title: "Integrado por Design", text: "EduInsights para avaliação e classificação de admissões, EduFlux para aprendizagem personalizada e EduAIKit para desafios baseados em simulação — capacidades integradas dentro do sistema Eduboxx, compartilhando um único registro de aprendizagem." },
        { title: "Operacionalmente Claro", text: "Uma plataforma. Um caminho de implantação. Uma história do piloto à escala." },
        { title: "Construído para Expansão", text: "O modelo é projetado para ir de implementações reais a oportunidades de expansão regional, estadual e nacional." },
      ],
    },
    products: {
      title: "Dentro da Plataforma Eduboxx",
      intro:
        "Eduboxx é a camada de implantação visível. Dentro dele está um ecossistema completo de tecnologia educacional — construído sobre pedagogia, movido por IA e alinhado à BNCC.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Infraestrutura Física",
          logoKey: "eduboxx",
          features: [
            "Camada de implantação local para escolas e redes",
            "Infraestrutura visível e tangível para confiança do setor público",
            "Suporta entrega de conteúdo em larga escala em ambientes com restrições",
            "Cria o gateway físico para adoção completa da plataforma",
          ],
        },
        {
          name: "EduFlux",
          tag: "Inteligência de Aprendizagem Personalizada",
          logoKey: "eduflux",
          features: [
            "Personalização generativa: lê o plano de aula de cada professor e cria a lição, o dever de casa e a prática para cada aluno — não retirados de um banco fixo",
            "Alinhado à BNCC: cada lição, tarefa e avaliação gerada mapeia para habilidades da BNCC — com relatórios por habilidade, para que professores e coordenadores vejam cobertura e domínio, não apenas atividade",
            "O tutor Teli dentro do trabalho: um tutor de IA que orienta os alunos em tempo real, com voz e texto",
            "Desenvolve Power Skills e Core Attributes — as competências transferíveis por trás de estruturas como as Approaches to Learning do IB",
          ],
        },
        {
          name: "EduInsights",
          tag: "Avaliação e Classificação de Admissões",
          logoKey: "eduinsights",
          features: [
            "Detecção de sinais além de uma única nota — revela como o candidato pensa, aprende e cresce",
            "Avaliação mais justa e completa, que compara candidatos por mais do que conteúdo memorizado",
            "Informa classificação e suporte desde o primeiro dia, não apenas aprovação ou rejeição",
            "Alimenta o mesmo registro de aprendizagem que acompanha o aluno até a sala de aula",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Aprendizado Baseado em Simulação · Parte do EduFlux",
          logoKey: "eduaikit",
          features: [
            "Desafios baseados em simulação: os alunos raciocinam em cenários e tomam decisões, em vez de apenas memorizar fatos",
            "Forte também fora das STEM — história, ciências sociais, ética, artes — onde bancos de questões fixos não bastam",
            "Gerados a partir da própria aula, personalizados para cada aluno",
            "Desenvolve raciocínio, perspectiva, uso de evidências e comunicação",
          ],
        },
      ],
      closing:
        "Juntos, esses componentes tornam o Eduboxx muito mais que uma caixa de entrega. Eles o tornam a expressão física de um sistema operacional educacional completo — generativo, liderado pela pedagogia, alinhado à BNCC e construído sobre um único registro de aprendizagem que acompanha cada aluno em todas as etapas.",
    },
    flow: {
      title: "Como o Sistema Funciona",
      intro:
        "O modelo Eduboxx é projetado para ir da implantação visível ao impacto educacional e operacional mensurável.",
      steps: [
        { title: "1. Implantar", text: "Instalar o Eduboxx em escolas piloto, campi, salas de aula ou regiões-alvo." },
        { title: "2. Entregar", text: "Fornecer acesso confiável a conteúdo educacional e serviços em ambientes escolares reais." },
        { title: "3. Avaliar e Classificar", text: "Usar o EduInsights para avaliar candidatos além de uma única nota e informar classificação e suporte desde o primeiro dia." },
        { title: "4. Personalizar a Aprendizagem", text: "Usar o EduFlux para gerar aulas personalizadas a partir do plano de cada professor — alinhadas à BNCC, com o tutor Teli orientando os alunos no trabalho." },
        { title: "5. Construir Raciocínio", text: "Usar o EduAIKit para transformar aulas em desafios baseados em simulação em todas as disciplinas, incluindo as não-STEM." },
        { title: "6. Escalar", text: "Expandir da implementação piloto para expansões municipais, estaduais ou nacionais." },
      ],
    },
    impact: {
      title: "Resultados e Impacto",
      intro:
        "Eduboxx cria valor em múltiplos níveis: operacionalmente, administrativamente, estrategicamente e educacionalmente.",
      cards: [
        { title: "Entrega Mais Confiável", text: "Estabilizar o acesso a conteúdo educacional em ambientes escolares do mundo real." },
        { title: "Aprendizagem Personalizada em Escala", text: "Cada aluno recebe instrução gerada a partir dos planos de aula dos seus professores, alinhada à BNCC, com o tutor Teli orientando o trabalho." },
        { title: "Sinal Mais Rico em Admissões", text: "Equipes de admissão recebem sinais mais completos do que uma única nota, e o que é aprendido sobre cada candidato informa a classificação e o suporte desde o primeiro dia." },
        { title: "Raciocínio Mais Profundo, em Todas as Disciplinas", text: "Desafios baseados em simulação desenvolvem competências transferíveis — inclusive em disciplinas não-STEM onde bancos de questões fixos não bastam." },
      ],
    },
    cases: {
      title: "Antes e Depois",
      intro:
        "Esses cenários mostram o que muda quando o Eduboxx substitui a entrega educacional fragmentada por um modelo de implantação integrado.",
      beforeLabel: "Antes",
      afterLabel: "Depois",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before: "Instituições precisavam de entrega confiável de vídeo educacional em larga escala em múltiplos ambientes, mas limitações de infraestrutura dificultavam o desempenho consistente.",
          after: "Eduboxx suportou entrega de streaming em larga escala, ajudando a atender milhares de alunos e possibilitando milhões de streams educacionais em ambientes reais de aprendizagem.",
        },
        {
          title: "Anhanguera / Kroton",
          before: "Grandes redes educacionais precisavam de uma forma escalável de entregar conteúdo em múltiplos sites com diferentes realidades operacionais.",
          after: "Eduboxx criou um modelo de infraestrutura estável e escalável capaz de suportar acesso simultâneo em ambientes educacionais distribuídos.",
        },
        {
          title: "Da Fragmentação ao Sistema",
          before: "Entrega, operações e visibilidade eram desconectadas, tornando a escala difícil e a aquisição mais difícil de justificar.",
          after: "Eduboxx criou uma história de implantação compreensível — infraestrutura visível por fora, inteligência integrada e poder de aprendizado por dentro.",
        },
      ],
    },
    licensing: {
      title: "Estrutura de Licenciamento Brasil",
      body:
        "DatanexAI atua como agente master de licenciamento para o Brasil, liderando com o Eduboxx como o sistema educacional visível e fisicamente demonstrável, enquanto entrega o valor integrado de EduInsights, EduFlux e EduAIKit como parte da mesma plataforma.",
      bullets: [
        "Um sistema, uma história de aquisição",
        "Implantação física com inteligência digital integrada",
        "Base comprovada para entrada no mercado brasileiro",
        "Escalável do piloto à grande expansão do setor público",
      ],
    },
    contact: {
      title: "Vamos Construir a Implantação Certa no Brasil",
      intro:
        "Inicie uma conversa sobre pilotos, implementações regionais, licenciamento ou implantação educacional de grande escala no setor público no Brasil.",
      form: {
        name: "Nome",
        org: "Organização",
        email: "E-mail",
        region: "País / Região",
        interest: "Interesse Principal",
        message: "Mensagem",
        options: [
          "Piloto Eduboxx",
          "Implantação Governamental",
          "Expansão Regional",
          "Parceria de Licenciamento",
        ],
        submit: "Enviar Consulta",
        select: "Selecione...",
      },
      note: "Este formulário está pronto para conectar ao seu CRM, endpoint de backend ou handler do WordPress.",
    },
    footer: {
      left: "DatanexAI",
      right: "Agente master de licenciamento no Brasil para o sistema educacional integrado Eduboxx.",
    },
  },

  es: {
    nav: {
      speed: "Velocidad Total",
      proof: "Pruebas",
      problem: "Problema",
      solution: "Solución",
      products: "Plataforma",
      flow: "Flujo",
      impact: "Impacto",
      cases: "Casos",
      licensing: "Licenciamiento Brasil",
      contact: "Contacto",
    },
    tagline: "Inteligencia de Aprendizaje. Construida sobre pedagogía. Impulsada por IA.",
    hero: {
      eyebrow: "DatanexAI • Agente Master de Licenciamiento en Brasil",
      title:
        "Eduboxx Convierte la Tecnología Educativa en Algo que los Gobiernos Pueden Ver, Confiar e Implementar",
      subtitle:
        "Eduboxx es la implementación física de un sistema completo de tecnología educativa para Brasil — combinando infraestructura visible con aprendizaje personalizado generado a partir de los planes de clase de sus profesores, evaluación y clasificación de admisiones, y desafíos basados en simulación, todos alineados al currículo de la BNCC en Brasil y construidos sobre pedagogía real.",
      primaryCta: "Solicitar un Piloto",
      secondaryCta: "Explorar Expansión en Brasil",
      stats: [
        "Comprobado en Brasil",
        "Millones de streams entregados",
        "Miles de estudiantes atendidos",
        "Construido para escala pública",
      ],
      sideTitle: "Por qué este mensaje funciona",
      sideBullets: [
        "Comienza con algo tangible y físicamente comprensible.",
        "Transforma una plataforma compleja en una historia clara de implementación.",
        "Demuestra que el sistema ya funcionó a escala en Brasil.",
        "Crea urgencia sin sonar abstracto o experimental.",
      ],
    },
    speed: {
      title: "Cada estudiante a máxima velocidad — sin sobrecargar su internet.",
      subtitle:
        "Eduboxx entrega el aprendizaje rico y personalizado de EduFlux a un aula entera al mismo tiempo — desde un nodo local dentro de la escuela, para que 30 estudiantes transmitiendo lecciones y simulaciones no lleven la conexión al límite.",
      problem:
        "Una escuela puede tener internet y aun así trabarse. Una conexión compartida no fue hecha para un aula entera tirando video, simulaciones y contenido personalizado en el mismo momento. Cuanto más atractivo el contenido, peor el cuello de botella. 'Conectado' no significa 'listo para 30 estudiantes a la vez.'",
      features: [
        "Entrega simultánea a escala de aula: Eduboxx sirve el contenido desde un nodo local por la red de la propia escuela, para que cada estudiante reciba aprendizaje a velocidad y calidad máximas al mismo tiempo — sin buffering, sin fila.",
        "Su conexión queda libre: el contenido pesado se entrega localmente; el enlace a internet solo lleva sincronización liviana. Otras clases, sistemas administrativos y el ancho de banda de la escuela no quedan ahogados.",
        "Hecho para contenido rico: lecciones en video, simulaciones de EduAIKit, la voz de Teli — los medios pesados y atractivos que la entrega solo por la nube tiene dificultad para servir a muchos estudiantes a la vez.",
        "Funciona también donde la conectividad es limitada: en escuelas con internet lenta, compartida o inestable, Eduboxx mantiene el aprendizaje funcionando a calidad máxima.",
        "Sincroniza cuando puede: el progreso y las señales vuelven al registro de aprendizaje cuando la conexión lo permite, manteniendo el registro completo.",
      ],
      differentiation:
        "Las plataformas que dependen solo de la nube se vuelven lentas justo cuando el aula más las necesita — cuando todos están aprendiendo al mismo tiempo. Eduboxx entrega a cada estudiante a máxima velocidad y deja su conexión a internet libre para todo lo demás.",
      cta: "Lleve aprendizaje a máxima velocidad a cada aula →",
    },
    proof: {
      title: "Comprobado a Escala en Brasil",
      intro:
        "Eduboxx no es un concepto. Ya ha sido implementado exitosamente en grandes entornos educativos brasileños, entregando streaming educativo a gran escala y acceso en aulas en configuraciones operativas reales.",
      stats: [
        "Millones de streams entregados",
        "Miles de estudiantes alcanzados",
        "Experiencia de implementación multi-sitio",
        "Validado en entornos educativos reales",
      ],
      institutionsTitle: "Implementado en",
      description:
        "Estas implementaciones demostraron que Eduboxx puede entregar contenido educativo confiable a escala en entornos de aprendizaje complejos — creando la base para el modelo integrado DatanexAI ahora posicionándose para Brasil.",
    },
    problem: {
      title: "El Problema",
      intro:
        "Los sistemas educativos quieren modernización, visibilidad y mejores resultados de aprendizaje — pero la mayoría de las iniciativas fracasan mucho antes de crear una transformación real.",
      cards: [
        { title: "Realidad de Infraestructura", text: "Muchas escuelas y redes aún operan en entornos donde el ancho de banda, la confiabilidad y el acceso simultáneo siguen siendo grandes limitaciones." },
        { title: "Sistemas Fragmentados", text: "Entrega de contenido, analítica, operaciones y soporte al aprendizaje frecuentemente están dispersos en herramientas desconectadas que crean fricción y confusión." },
        { title: "Narrativas Débiles de Adquisición", text: "Los compradores del sector público frecuentemente tienen dificultad para confiar o evaluar soluciones presentadas solo como software, dashboards o abstracciones en la nube." },
        { title: "Fallo del Piloto a la Escala", text: "Incluso pilotos prometedores frecuentemente colapsan porque el modelo nunca fue diseñado para convertirse en un sistema operativo escalable." },
      ],
    },
    solution: {
      title: "Por Qué Eduboxx Gana",
      intro:
        "Eduboxx gana porque comienza donde comienza la confianza: con algo visible, físico e implementable — y luego entrega la plataforma completa dentro de esa implementación.",
      cards: [
        { title: "Infraestructura Visible", text: "Eduboxx da a gobiernos e instituciones algo tangible para evaluar, implementar y expandir." },
        { title: "Integrado por Diseño", text: "EduInsights para evaluación y clasificación de admisiones, EduFlux para aprendizaje personalizado y EduAIKit para desafíos basados en simulación — capacidades integradas dentro del sistema Eduboxx, compartiendo un único registro de aprendizaje." },
        { title: "Operacionalmente Claro", text: "Una plataforma. Un camino de implementación. Una historia del piloto a la escala." },
        { title: "Construido para Expansión", text: "El modelo está diseñado para ir de implementaciones reales a oportunidades de expansión regional, estatal y nacional." },
      ],
    },
    products: {
      title: "Dentro de la Plataforma Eduboxx",
      intro:
        "Eduboxx es la capa de implementación visible. Dentro de él se encuentra un ecosistema completo de tecnología educativa — construido sobre pedagogía, impulsado por IA y alineado al currículo de la BNCC.",
      cards: [
        {
          name: "Eduboxx",
          tag: "Infraestructura Física",
          logoKey: "eduboxx",
          features: [
            "Capa de implementación local para escuelas y redes",
            "Infraestructura visible y tangible para confianza del sector público",
            "Soporta entrega de contenido a gran escala en entornos con restricciones",
            "Crea la puerta de entrada física para adopción completa de la plataforma",
          ],
        },
        {
          name: "EduFlux",
          tag: "Inteligencia de Aprendizaje Personalizado",
          logoKey: "eduflux",
          features: [
            "Personalización generativa: lee el plan de cada profesor y crea la lección, la tarea y la práctica para cada estudiante — no extraídos de un banco fijo",
            "Alineado a la BNCC: cada lección, tarea y evaluación generada se mapea a habilidades de la BNCC — con reportes por habilidad, para que profesores y coordinadores vean cobertura y dominio, no solo actividad",
            "El tutor Teli dentro del trabajo: un tutor de IA que guía a los estudiantes en tiempo real, con voz y texto",
            "Desarrolla Power Skills y Core Attributes — las competencias transferibles detrás de marcos como las Approaches to Learning del IB",
          ],
        },
        {
          name: "EduInsights",
          tag: "Evaluación y Clasificación de Admisiones",
          logoKey: "eduinsights",
          features: [
            "Detección de señales más allá de una sola calificación — revela cómo el postulante piensa, aprende y crece",
            "Evaluación más justa y completa, que compara postulantes por más que contenido memorizado",
            "Informa la clasificación y el apoyo desde el primer día, no solo admisión o rechazo",
            "Alimenta el mismo registro de aprendizaje que acompaña al estudiante hasta el aula",
          ],
        },
        {
          name: "EduAIKit",
          tag: "Aprendizaje Basado en Simulación · Parte de EduFlux",
          logoKey: "eduaikit",
          features: [
            "Desafíos basados en simulación: los estudiantes razonan en escenarios y toman decisiones, en lugar de solo recordar hechos",
            "Fuerte también fuera de STEM — historia, ciencias sociales, ética, artes — donde los bancos de preguntas fijos se quedan cortos",
            "Generados a partir de la propia lección, personalizados para cada estudiante",
            "Desarrolla razonamiento, perspectiva, uso de evidencia y comunicación",
          ],
        },
      ],
      closing:
        "Juntos, estos componentes hacen de Eduboxx mucho más que una caja de entrega. Lo convierten en la expresión física de un sistema operativo educativo completo — generativo, liderado por la pedagogía, alineado a la BNCC y construido sobre un único registro de aprendizaje que acompaña a cada estudiante en cada etapa.",
    },
    flow: {
      title: "Cómo Funciona el Sistema",
      intro:
        "El modelo Eduboxx está diseñado para ir de la implementación visible al impacto educativo y operativo medible.",
      steps: [
        { title: "1. Implementar", text: "Instalar Eduboxx en escuelas piloto, campus, aulas o regiones objetivo." },
        { title: "2. Entregar", text: "Proporcionar acceso confiable a contenido educativo y servicios en entornos escolares reales." },
        { title: "3. Evaluar y Clasificar", text: "Usar EduInsights para evaluar postulantes más allá de una sola calificación e informar la clasificación y el apoyo desde el primer día." },
        { title: "4. Personalizar el Aprendizaje", text: "Usar EduFlux para generar lecciones personalizadas a partir del plan de cada profesor — alineadas a la BNCC, con el tutor Teli guiando a los estudiantes en el trabajo." },
        { title: "5. Construir Razonamiento", text: "Usar EduAIKit para convertir lecciones en desafíos basados en simulación en todas las materias, incluyendo no-STEM." },
        { title: "6. Escalar", text: "Expandir de la implementación piloto a expansiones municipales, estatales o nacionales." },
      ],
    },
    impact: {
      title: "Resultados e Impacto",
      intro:
        "Eduboxx crea valor en múltiples niveles: operacionalmente, administrativamente, estratégicamente y educacionalmente.",
      cards: [
        { title: "Entrega Más Confiable", text: "Estabilizar el acceso a contenido educativo en entornos escolares del mundo real." },
        { title: "Aprendizaje Personalizado a Escala", text: "Cada estudiante recibe instrucción generada a partir de los planes de clase de sus profesores, alineada a la BNCC, con el tutor Teli guiando el trabajo." },
        { title: "Señal Más Rica en Admisiones", text: "Los equipos de admisiones reciben señales más completas que una sola calificación, y lo aprendido sobre cada postulante informa la clasificación y el apoyo desde el primer día." },
        { title: "Razonamiento Más Profundo, en Cada Materia", text: "Desafíos basados en simulación desarrollan competencias transferibles — incluso en materias no-STEM donde los bancos de preguntas fijos se quedan cortos." },
      ],
    },
    cases: {
      title: "Antes y Después",
      intro:
        "Estos escenarios muestran qué cambia cuando Eduboxx reemplaza la entrega educativa fragmentada con un modelo de implementación integrado.",
      beforeLabel: "Antes",
      afterLabel: "Después",
      cards: [
        {
          title: "Prima / LFG / Damásio",
          before: "Las instituciones necesitaban entrega confiable de video educativo a gran escala en múltiples entornos, pero las limitaciones de infraestructura dificultaban el rendimiento consistente.",
          after: "Eduboxx soportó entrega de streaming a gran escala, ayudando a atender miles de estudiantes y posibilitando millones de streams educativos en entornos reales de aprendizaje.",
        },
        {
          title: "Anhanguera / Kroton",
          before: "Grandes redes educativas necesitaban una forma escalable de entregar contenido en múltiples sitios con diferentes realidades operativas.",
          after: "Eduboxx creó un modelo de infraestructura estable y escalable capaz de soportar acceso simultáneo en entornos educativos distribuidos.",
        },
        {
          title: "De la Fragmentación al Sistema",
          before: "Entrega, operaciones y visibilidad estaban desconectadas, haciendo la escala difícil y la adquisición más difícil de justificar.",
          after: "Eduboxx creó una historia de implementación comprensible — infraestructura visible por fuera, inteligencia integrada y poder de aprendizaje por dentro.",
        },
      ],
    },
    licensing: {
      title: "Estructura de Licenciamiento Brasil",
      body:
        "DatanexAI actúa como agente master de licenciamiento para Brasil, liderando con Eduboxx como el sistema educativo visible y físicamente demostrable, mientras entrega el valor integrado de EduInsights, EduFlux y EduAIKit como parte de la misma plataforma.",
      bullets: [
        "Un sistema, una historia de adquisición",
        "Implementación física con inteligencia digital integrada",
        "Base comprobada para entrada al mercado brasileño",
        "Escalable del piloto a gran expansión del sector público",
      ],
    },
    contact: {
      title: "Construyamos la Implementación Correcta en Brasil",
      intro:
        "Inicie una conversación sobre pilotos, implementaciones regionales, licenciamiento o implementación educativa de gran escala en el sector público en Brasil.",
      form: {
        name: "Nombre",
        org: "Organización",
        email: "Correo Electrónico",
        region: "País / Región",
        interest: "Interés Principal",
        message: "Mensaje",
        options: [
          "Piloto Eduboxx",
          "Implementación Gubernamental",
          "Expansión Regional",
          "Asociación de Licenciamiento",
        ],
        submit: "Enviar Consulta",
        select: "Seleccionar...",
      },
      note: "Este formulario está listo para conectar a su CRM, endpoint de backend o handler de WordPress.",
    },
    footer: {
      left: "DatanexAI",
      right: "Agente master de licenciamiento en Brasil para el sistema educativo integrado Eduboxx.",
    },
  },
};

function usePageStyles() {
  useEffect(() => {
    const id = "datanexai-branded-styles-v4";
    if (document.getElementById(id)) return;

    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      html { scroll-behavior: smooth; }
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: ${BRAND.bg} !important;
        font-family: Inter, "Segoe UI", Arial, Helvetica, sans-serif;
      }

      .dnx-app {
        position: relative;
        min-height: 100vh;
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
        color: ${BRAND.text};
        background:
          radial-gradient(1100px 700px at 12% -10%, rgba(59,130,246,0.18), transparent 60%),
          radial-gradient(900px 600px at 92% 8%, rgba(6,182,212,0.14), transparent 65%),
          radial-gradient(900px 600px at 50% 110%, rgba(59,130,246,0.10), transparent 65%),
          linear-gradient(180deg, #f8fbff 0%, #eef6ff 50%, #e8f2fb 100%);
      }

      .dnx-app, .dnx-app * { box-sizing: border-box; }
      .dnx-app a { text-decoration: none; color: inherit; }
      .dnx-app img { max-width: 100%; height: auto; }
      .dnx-app input, .dnx-app textarea, .dnx-app button, .dnx-app select { font: inherit; }

      .dnx-card-hover {
        transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease, filter .35s ease;
      }
      .dnx-card-hover:hover {
        transform: translateY(-5px);
        box-shadow: 0 28px 70px rgba(15,23,42,.14);
        border-color: rgba(15,23,42,.16);
      }

      @keyframes dnx-floatY {
        0% { transform: translateY(0px); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0px); }
      }
      @keyframes dnx-pulse {
        0% { opacity: .12; transform: scale(1); }
        50% { opacity: .22; transform: scale(1.05); }
        100% { opacity: .12; transform: scale(1); }
      }
      .dnx-float { animation: dnx-floatY 9s ease-in-out infinite; }
      .dnx-pulse { animation: dnx-pulse 8s ease-in-out infinite; }

      .dnx-input::placeholder,
      .dnx-app textarea::placeholder {
        color: rgba(15,23,42,0.45);
      }

      @media (max-width: 1024px) {
        .dnx-grid-hero,
        .dnx-grid-two,
        .dnx-grid-product,
        .dnx-grid-cta {
          grid-template-columns: minmax(0, 1fr) !important;
        }
        .dnx-grid-four,
        .dnx-grid-three {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
        }
        .dnx-nav-desktop { display: none !important; }
        .dnx-nav-mobile { display: inline-flex !important; }
        .dnx-glass {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        .dnx-float, .dnx-pulse { animation: none !important; }
        .dnx-section { padding: 64px 18px !important; }
      }

      @media (prefers-reduced-motion: reduce) {
        .dnx-float, .dnx-pulse, .dnx-card-hover {
          animation: none !important; transition: none !important;
        }
      }

      @media (max-width: 720px) {
        .dnx-grid-four,
        .dnx-grid-three {
          grid-template-columns: minmax(0, 1fr) !important;
        }
        .dnx-title { font-size: 38px !important; line-height: 1.05 !important; letter-spacing: -1px !important; }
        .dnx-app h2 { font-size: 26px !important; line-height: 1.18 !important; letter-spacing: -0.3px !important; }
        .dnx-app p, .dnx-app li { font-size: 15px !important; }
        .dnx-section { padding: 52px 16px !important; }
        .dnx-hero-buttons, .dnx-cta-buttons {
          flex-direction: column !important; align-items: stretch !important;
        }
        .dnx-hero-buttons a, .dnx-cta-buttons a {
          width: 100% !important; text-align: center !important;
        }
        .dnx-header-logo { height: 36px !important; }
        .dnx-hero-stats { grid-template-columns: 1fr !important; }
        .dnx-product-logo-frame { min-height: 110px !important; }
        .dnx-product-logo-img { height: 64px !important; }
      }

      @media (max-width: 420px) {
        .dnx-title { font-size: 32px !important; }
        .dnx-app h2 { font-size: 24px !important; }
        .dnx-section { padding: 44px 14px !important; }
      }
    `;
    document.head.appendChild(style);
  }, []);
}

function Section({ id, children, style = {} }) {
  return (
    <section
      id={id}
      className="dnx-section"
      style={{
        maxWidth: 1280,
        margin: "0 auto",
        padding: "88px 20px",
        position: "relative",
        ...style,
      }}
    >
      {children}
    </section>
  );
}

function Glass({ children, style = {}, className = "" }) {
  return (
    <div
      className={`dnx-glass ${className}`}
      style={{
        background: BRAND.card,
        border: `1px solid ${BRAND.line}`,
        borderRadius: 24,
        boxShadow: BRAND.shadow,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Glow({ left, right, top, bottom, size = 260, from = BRAND.sky, to = BRAND.cyanLight, opacity = 0.22 }) {
  return (
    <div
      className="dnx-pulse"
      style={{
        position: "absolute",
        left, right, top, bottom,
        width: size, height: size,
        borderRadius: 999,
        background: `radial-gradient(circle at 30% 30%, ${from}, ${to})`,
        filter: "blur(70px)",
        opacity,
        pointerEvents: "none",
      }}
    />
  );
}

function Kicker({ children, color = BRAND.ink }) {
  return (
    <div style={{ color, fontSize: 12, textTransform: "uppercase", letterSpacing: 2, fontWeight: 800 }}>
      {children}
    </div>
  );
}

function LangSwitcher({ lang, setLang, compact = false }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: 4,
        borderRadius: 999,
        border: `1px solid ${BRAND.line}`,
        background: "rgba(255,255,255,0.95)",
      }}
    >
      {["en", "pt", "es"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          style={{
            border: 0,
            background: lang === code ? `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})` : "transparent",
            color: lang === code ? BRAND.white : BRAND.muted,
            padding: compact ? "5px 10px" : "6px 12px",
            borderRadius: 999,
            cursor: "pointer",
            fontWeight: 800,
            fontSize: compact ? 11 : 12,
            letterSpacing: 0.5,
          }}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function MobileMenu({ open, onClose, links, lang, setLang, t }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 320,
          maxWidth: "88vw",
          height: "100%",
          marginLeft: "auto",
          background: "linear-gradient(180deg, #ffffff 0%, #f1f7ff 100%)",
          borderLeft: `1px solid ${BRAND.line}`,
          padding: 22,
          overflowY: "auto",
          boxShadow: "-20px 0 50px rgba(15,23,42,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ color: BRAND.text, fontWeight: 800, fontSize: 14, letterSpacing: 1, textTransform: "uppercase" }}>
            Menu
          </div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              background: "rgba(15,23,42,0.06)",
              border: `1px solid ${BRAND.line}`,
              color: BRAND.text,
              borderRadius: 12,
              padding: "8px 12px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <LangSwitcher lang={lang} setLang={setLang} />
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {links.map(([label, id]) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={onClose}
              style={{
                color: BRAND.text,
                fontWeight: 600,
                background: BRAND.white,
                border: `1px solid ${BRAND.line}`,
                borderRadius: 14,
                padding: "13px 14px",
                fontSize: 14.5,
              }}
            >
              {label}
            </a>
          ))}

          <a
            href="#contact"
            onClick={onClose}
            style={{
              marginTop: 6,
              padding: "14px 16px",
              borderRadius: 14,
              background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
              color: BRAND.white,
              fontWeight: 800,
              textAlign: "center",
              boxShadow: "0 12px 28px rgba(29,78,216,0.28)",
            }}
          >
            {t.nav.contact}
          </a>
        </div>
      </div>
    </div>
  );
}

function Header({ t, lang, setLang, onOpenMenu }) {
  const links = [
    [t.nav.speed, "speed"],
    [t.nav.proof, "proof"],
    [t.nav.problem, "problem"],
    [t.nav.solution, "solution"],
    [t.nav.products, "products"],
    [t.nav.flow, "flow"],
    [t.nav.impact, "impact"],
    [t.nav.cases, "cases"],
    [t.nav.licensing, "licensing"],
  ];

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        background: "rgba(255,255,255,0.88)",
        borderBottom: `1px solid ${BRAND.line}`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 4px 18px rgba(15, 23, 42, 0.04)",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <a href="#top" style={{ display: "inline-flex", alignItems: "center" }}>
          <img
            src={IMAGES.datanex}
            alt="DatanexAI"
            className="dnx-header-logo"
            style={{
              height: 44,
              width: "auto",
              objectFit: "contain",
            }}
          />
        </a>

        {/* Desktop nav */}
        <nav className="dnx-nav-desktop" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {links.map(([label, id]) => (
            <a key={id} href={`#${id}`} style={{ color: BRAND.muted, fontWeight: 600, fontSize: 13.5 }}>
              {label}
            </a>
          ))}

          <span style={{ width: 1, height: 20, background: BRAND.line }} />

          <LangSwitcher lang={lang} setLang={setLang} />

          <a
            href="#contact"
            style={{
              padding: "12px 18px",
              borderRadius: 999,
              background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
              color: BRAND.white,
              fontWeight: 800,
              fontSize: 13,
              boxShadow: "0 12px 28px rgba(29,78,216,0.28)",
            }}
          >
            {t.nav.contact}
          </a>
        </nav>

        {/* Mobile cluster: lang + hamburger (always visible at ≤1024px) */}
        <div className="dnx-nav-mobile" style={{ display: "none", alignItems: "center", gap: 8 }}>
          <LangSwitcher lang={lang} setLang={setLang} compact />
          <button
            onClick={onOpenMenu}
            aria-label="Open menu"
            style={{
              background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
              border: 0,
              color: BRAND.white,
              borderRadius: 12,
              padding: "10px 12px",
              cursor: "pointer",
              fontWeight: 800,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 10px 22px rgba(29,78,216,0.28)",
            }}
          >
            <span style={{ display: "inline-grid", gap: 3 }}>
              <span style={{ width: 18, height: 2, background: "#fff", borderRadius: 2 }} />
              <span style={{ width: 18, height: 2, background: "#fff", borderRadius: 2 }} />
              <span style={{ width: 18, height: 2, background: "#fff", borderRadius: 2 }} />
            </span>
          </button>
        </div>
      </div>
    </header>
  );
}

function Hero({ t }) {
  return (
    <Section id="top" style={{ paddingTop: 96, paddingBottom: 72, overflow: "hidden" }}>
      <Glow left={-80} top={20} size={300} from={BRAND.sky} to={BRAND.cyanLight} />
      <Glow right={-60} top={60} size={340} from={BRAND.cyanLight} to={BRAND.blue} opacity={0.18} />

      <div className="dnx-grid-hero" style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 40, alignItems: "center" }}>
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "8px 14px",
              borderRadius: 999,
              background: BRAND.chipBg,
              border: `1px solid ${BRAND.chipBorder}`,
              color: BRAND.blue,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            {t.hero.eyebrow}
          </div>

          <h1
            className="dnx-title"
            style={{
              marginTop: 22,
              marginBottom: 0,
              fontSize: 64,
              lineHeight: 1.04,
              letterSpacing: -2.2,
              color: BRAND.text,
              fontWeight: 800,
            }}
          >
            {t.hero.title}
          </h1>

          <div
            style={{
              marginTop: 18,
              fontSize: 22,
              lineHeight: 1.4,
              fontWeight: 800,
              background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: 0.2,
              maxWidth: 820,
            }}
          >
            {t.tagline}
          </div>

          <p style={{ marginTop: 16, fontSize: 18, lineHeight: 1.75, color: BRAND.muted, maxWidth: 820 }}>
            {t.hero.subtitle}
          </p>

          <div className="dnx-hero-buttons" style={{ marginTop: 26, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <a
              href="#contact"
              style={{
                padding: "16px 22px",
                borderRadius: 16,
                background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
                color: BRAND.white,
                fontWeight: 800,
                boxShadow: "0 16px 32px rgba(29,78,216,0.22)",
              }}
            >
              {t.hero.primaryCta}
            </a>
            <a
              href="#licensing"
              style={{
                padding: "16px 22px",
                borderRadius: 16,
                background: BRAND.white,
                color: BRAND.text,
                fontWeight: 800,
                border: `1px solid ${BRAND.line}`,
                boxShadow: "0 8px 22px rgba(15,23,42,0.06)",
              }}
            >
              {t.hero.secondaryCta}
            </a>
          </div>

          <div className="dnx-hero-stats" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, maxWidth: 600 }}>
            {t.hero.stats.map((item) => (
              <div
                key={item}
                style={{
                  padding: "12px 14px",
                  borderRadius: 14,
                  background: BRAND.softTile,
                  border: `1px solid ${BRAND.line}`,
                  color: BRAND.text,
                  fontSize: 13.5,
                  fontWeight: 700,
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <Glass className="dnx-card-hover" style={{ overflow: "hidden", padding: 22 }}>
          <img
            src={IMAGES.hero}
            alt="DatanexAI Hero"
            className="dnx-float"
            style={{
              width: "100%",
              display: "block",
              borderRadius: 18,
              boxShadow: "0 22px 60px rgba(15,23,42,0.12)",
            }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div style={{ marginTop: 18 }}>
            <Kicker color={BRAND.ink}>{t.hero.sideTitle}</Kicker>
            <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0", display: "grid", gap: 10 }}>
              {t.hero.sideBullets.map((b) => (
                <li
                  key={b}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: BRAND.softTile,
                    border: `1px solid ${BRAND.line}`,
                    color: BRAND.text,
                    lineHeight: 1.55,
                    fontSize: 14,
                  }}
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </Glass>
      </div>
    </Section>
  );
}

function SpeedSection({ t }) {
  return (
    <Section id="speed">
      <Kicker>{t.nav.speed}</Kicker>
      <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
        {t.speed.title}
      </h2>
      <p style={{ marginTop: 14, maxWidth: 960, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
        {t.speed.subtitle}
      </p>

      <div className="dnx-grid-two" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "0.95fr 1.05fr", gap: 28, alignItems: "stretch" }}>
        <Glass className="dnx-card-hover" style={{ padding: 26 }}>
          <Kicker color={BRAND.cyan}>The bottleneck</Kicker>
          <p style={{ marginTop: 12, color: BRAND.text, lineHeight: 1.85, fontSize: 16 }}>{t.speed.problem}</p>
        </Glass>

        <div style={{ display: "grid", gap: 12 }}>
          {t.speed.features.map((f) => (
            <Glass key={f} className="dnx-card-hover" style={{ padding: 16 }}>
              <div style={{ color: BRAND.text, lineHeight: 1.7, fontSize: 15 }}>{f}</div>
            </Glass>
          ))}
        </div>
      </div>

      <p style={{ marginTop: 24, color: BRAND.muted, fontSize: 17, lineHeight: 1.85, maxWidth: 960 }}>
        {t.speed.differentiation}
      </p>

      <div className="dnx-cta-buttons" style={{ marginTop: 22, display: "flex", gap: 14, flexWrap: "wrap" }}>
        <a
          href="#contact"
          style={{
            padding: "14px 22px",
            borderRadius: 16,
            background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
            color: BRAND.white,
            fontWeight: 800,
            boxShadow: "0 16px 32px rgba(29,78,216,0.22)",
          }}
        >
          {t.speed.cta}
        </a>
      </div>
    </Section>
  );
}

function ProofSection({ t }) {
  const logos = [
    { src: IMAGES.kroton, alt: "Kroton" },
    { src: IMAGES.anhanguera, alt: "Anhanguera" },
    { src: IMAGES.lfg, alt: "LFG" },
    { src: IMAGES.damasio, alt: "Damásio" },
  ];

  return (
    <Section id="proof">
      <Kicker>{t.nav.proof}</Kicker>
      <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
        {t.proof.title}
      </h2>
      <p style={{ marginTop: 14, maxWidth: 960, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
        {t.proof.intro}
      </p>

      <div className="dnx-grid-four" style={{ marginTop: 26, display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 16 }}>
        {t.proof.stats.map((s) => (
          <Glass key={s} className="dnx-card-hover" style={{ padding: 22, textAlign: "center" }}>
            <div style={{ color: BRAND.blue, fontWeight: 800, fontSize: 17, lineHeight: 1.4 }}>{s}</div>
          </Glass>
        ))}
      </div>

      <Glass className="dnx-card-hover" style={{ marginTop: 24, padding: 28 }}>
        <Kicker color={BRAND.ink}>{t.proof.institutionsTitle}</Kicker>
        <div style={{ marginTop: 18, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 24 }}>
          {logos.map((l) => (
            <div
              key={l.alt}
              style={{
                background: BRAND.white,
                padding: "10px 16px",
                borderRadius: 14,
                border: `1px solid ${BRAND.line}`,
                boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
              }}
            >
              <img
                src={l.src}
                alt={l.alt}
                style={{
                  height: 48,
                  maxWidth: 160,
                  objectFit: "contain",
                  display: "block",
                  filter: "grayscale(60%)",
                  opacity: 0.85,
                  transition: "filter .2s, opacity .2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = "grayscale(0%)"; e.currentTarget.style.opacity = 1; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "grayscale(60%)"; e.currentTarget.style.opacity = 0.85; }}
              />
            </div>
          ))}
        </div>
        <p style={{ marginTop: 18, color: BRAND.muted, lineHeight: 1.85, fontSize: 16 }}>{t.proof.description}</p>
      </Glass>
    </Section>
  );
}

function ProblemSection({ t }) {
  return (
    <Section id="problem">
      <Kicker color={BRAND.cyan}>{t.nav.problem}</Kicker>
      <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
        {t.problem.title}
      </h2>
      <p style={{ marginTop: 14, maxWidth: 960, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
        {t.problem.intro}
      </p>

      <div className="dnx-grid-four" style={{ marginTop: 26, display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 18 }}>
        {t.problem.cards.map((card) => (
          <Glass key={card.title} className="dnx-card-hover" style={{ padding: 22 }}>
            <div style={{ color: BRAND.text, fontSize: 19, fontWeight: 700 }}>{card.title}</div>
            <p style={{ marginTop: 10, color: BRAND.muted, lineHeight: 1.75 }}>{card.text}</p>
          </Glass>
        ))}
      </div>
    </Section>
  );
}

function SolutionSection({ t }) {
  return (
    <Section id="solution">
      <div className="dnx-grid-two" style={{ display: "grid", gridTemplateColumns: "0.95fr 1.05fr", gap: 36, alignItems: "center" }}>
        <div>
          <Kicker>{t.nav.solution}</Kicker>
          <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
            {t.solution.title}
          </h2>
          <p style={{ marginTop: 14, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
            {t.solution.intro}
          </p>
        </div>

        <Glass className="dnx-card-hover" style={{ overflow: "hidden", padding: 18, background: "rgba(255,255,255,0.10)" }}>
          <img
            src={IMAGES.ecosystem}
            alt="DatanexAI Ecosystem"
            className="dnx-float"
            style={{ width: "100%", display: "block", borderRadius: 18, boxShadow: "0 22px 60px rgba(14,5,38,0.4)" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </Glass>
      </div>

      <div className="dnx-grid-four" style={{ marginTop: 32, display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 18 }}>
        {t.solution.cards.map((card) => (
          <Glass key={card.title} className="dnx-card-hover" style={{ padding: 22 }}>
            <div style={{ color: BRAND.text, fontSize: 19, fontWeight: 700 }}>{card.title}</div>
            <p style={{ marginTop: 10, color: BRAND.muted, lineHeight: 1.75 }}>{card.text}</p>
          </Glass>
        ))}
      </div>
    </Section>
  );
}

function ProductsSection({ t }) {
  const ACCENTS = {
    eduboxx: BRAND.blue,
    eduflux: BRAND.cyan,
    eduinsights: BRAND.ink,
    eduaikit: BRAND.sky,
  };

  return (
    <Section id="products">
      <Kicker>{t.nav.products}</Kicker>
      <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
        {t.products.title}
      </h2>
      <p style={{ marginTop: 14, maxWidth: 960, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
        {t.products.intro}
      </p>

      <div className="dnx-grid-four" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 18 }}>
        {t.products.cards.map((card) => {
          const accent = ACCENTS[card.logoKey] || BRAND.lavender;
          return (
            <Glass key={card.name} className="dnx-card-hover" style={{ padding: 22, display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  display: "inline-block",
                  padding: "6px 10px",
                  borderRadius: 999,
                  background: `${accent}22`,
                  color: accent,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  alignSelf: "flex-start",
                }}
              >
                {card.tag}
              </div>

              <div
                className="dnx-product-logo-frame"
                style={{
                  marginTop: 14,
                  padding: "16px 16px",
                  background: BRAND.softTile,
                  borderRadius: 14,
                  border: `1px solid ${BRAND.line}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 96,
                }}
              >
                <img
                  src={IMAGES[card.logoKey]}
                  alt={card.name}
                  className="dnx-product-logo-img"
                  style={{ height: 60, maxWidth: "100%", objectFit: "contain" }}
                />
              </div>

              <div style={{ marginTop: 14, fontSize: 22, fontWeight: 800, color: accent }}>{card.name}</div>

              <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 10 }}>
                {card.features.map((f) => (
                  <li
                    key={f}
                    style={{
                      color: BRAND.text,
                      lineHeight: 1.6,
                      fontSize: 14,
                      paddingLeft: 16,
                      position: "relative",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 8,
                        width: 7,
                        height: 7,
                        borderRadius: 999,
                        background: accent,
                        boxShadow: `0 0 10px ${accent}55`,
                      }}
                    />
                    {f}
                  </li>
                ))}
              </ul>
            </Glass>
          );
        })}
      </div>

      <p style={{ marginTop: 22, color: BRAND.muted, fontSize: 17, lineHeight: 1.85, maxWidth: 1000 }}>
        {t.products.closing}
      </p>
    </Section>
  );
}

function FlowSection({ t }) {
  return (
    <Section id="flow" style={{ overflow: "hidden" }}>
      <Glow right={-60} top={30} size={300} from={BRAND.cyanLight} to={BRAND.blue} opacity={0.14} />
      <Kicker>{t.nav.flow}</Kicker>
      <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
        {t.flow.title}
      </h2>
      <p style={{ marginTop: 14, maxWidth: 960, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
        {t.flow.intro}
      </p>

      <div className="dnx-grid-three" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 18 }}>
        {t.flow.steps.map((step, idx) => (
          <Glass key={step.title} className="dnx-card-hover" style={{ padding: 22 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 999,
                background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: BRAND.white,
                fontWeight: 800,
                fontSize: 18,
                boxShadow: "0 12px 24px rgba(29,78,216,0.28)",
              }}
            >
              {idx + 1}
            </div>
            <div style={{ marginTop: 14, color: BRAND.text, fontSize: 18, fontWeight: 700 }}>
              {step.title.replace(/^\d+\.\s*/, "")}
            </div>
            <p style={{ marginTop: 8, color: BRAND.muted, lineHeight: 1.75 }}>{step.text}</p>
          </Glass>
        ))}
      </div>
    </Section>
  );
}

function ImpactSection({ t }) {
  return (
    <Section id="impact">
      <div className="dnx-grid-two" style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 36, alignItems: "center" }}>
        <Glass className="dnx-card-hover" style={{ overflow: "hidden", padding: 18, background: "rgba(255,255,255,0.10)" }}>
          <img
            src={IMAGES.dashboard}
            alt="DatanexAI Impact"
            className="dnx-float"
            style={{ width: "100%", display: "block", borderRadius: 18, boxShadow: "0 22px 60px rgba(14,5,38,0.4)" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </Glass>

        <div>
          <Kicker>{t.nav.impact}</Kicker>
          <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
            {t.impact.title}
          </h2>
          <p style={{ marginTop: 14, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
            {t.impact.intro}
          </p>
        </div>
      </div>

      <div className="dnx-grid-four" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 18 }}>
        {t.impact.cards.map((card) => (
          <Glass key={card.title} className="dnx-card-hover" style={{ padding: 22 }}>
            <div style={{ color: BRAND.text, fontSize: 19, fontWeight: 700 }}>{card.title}</div>
            <p style={{ marginTop: 10, color: BRAND.muted, lineHeight: 1.75 }}>{card.text}</p>
          </Glass>
        ))}
      </div>
    </Section>
  );
}

function CasesSection({ t }) {
  return (
    <Section id="cases">
      <Kicker>{t.nav.cases}</Kicker>
      <h2 style={{ marginTop: 12, fontSize: 44, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
        {t.cases.title}
      </h2>
      <p style={{ marginTop: 14, maxWidth: 960, color: BRAND.muted, fontSize: 18, lineHeight: 1.85 }}>
        {t.cases.intro}
      </p>

      <div className="dnx-grid-three" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 18 }}>
        {t.cases.cards.map((card) => (
          <Glass key={card.title} className="dnx-card-hover" style={{ padding: 24 }}>
            <div style={{ color: BRAND.text, fontSize: 20, fontWeight: 700 }}>{card.title}</div>

            <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
              <div style={{ padding: "12px 14px", borderRadius: 14, background: BRAND.softTile, border: `1px solid ${BRAND.line}` }}>
                <Kicker color={BRAND.cyan}>{t.cases.beforeLabel}</Kicker>
                <p style={{ marginTop: 6, color: BRAND.muted, lineHeight: 1.8, fontSize: 14.5 }}>{card.before}</p>
              </div>
              <div style={{ padding: "12px 14px", borderRadius: 14, background: "linear-gradient(135deg, rgba(29,78,216,0.08), rgba(8,145,178,0.08))", border: `1px solid ${BRAND.chipBorder}` }}>
                <Kicker color={BRAND.blue}>{t.cases.afterLabel}</Kicker>
                <p style={{ marginTop: 6, color: BRAND.text, lineHeight: 1.8, fontSize: 14.5, fontWeight: 600 }}>{card.after}</p>
              </div>
            </div>
          </Glass>
        ))}
      </div>
    </Section>
  );
}

function LicensingSection({ t }) {
  return (
    <Section id="licensing" style={{ overflow: "hidden" }}>
      <Glass
        className="dnx-card-hover"
        style={{
          padding: 34,
          overflow: "hidden",
          position: "relative",
          background: "linear-gradient(135deg, rgba(219, 234, 254, 0.95), rgba(207, 250, 254, 0.95))",
        }}
      >
        <Glow left={-20} top={-20} size={220} from={BRAND.sky} to={BRAND.cyanLight} opacity={0.18} />
        <Glow right={-20} bottom={-20} size={220} from={BRAND.cyanLight} to={BRAND.blue} opacity={0.16} />
        <div style={{ position: "relative", zIndex: 2 }}>
          <Kicker>{t.nav.licensing}</Kicker>
          <h2 style={{ marginTop: 12, fontSize: 42, lineHeight: 1.08, color: BRAND.text, letterSpacing: -0.6 }}>
            {t.licensing.title}
          </h2>
          <p style={{ marginTop: 14, color: BRAND.muted, fontSize: 18, lineHeight: 1.85, maxWidth: 1000 }}>
            {t.licensing.body}
          </p>

          <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12 }}>
            {t.licensing.bullets.map((bullet) => (
              <div
                key={bullet}
                style={{
                  padding: "14px 16px",
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.85)",
                  border: `1px solid ${BRAND.line}`,
                  color: BRAND.text,
                  lineHeight: 1.6,
                  fontWeight: 600,
                  boxShadow: "0 6px 16px rgba(15,23,42,0.04)",
                }}
              >
                {bullet}
              </div>
            ))}
          </div>
        </div>
      </Glass>
    </Section>
  );
}

function ContactSection({ t }) {
  const [formData, setFormData] = useState({
    name: "", org: "", email: "", region: "", interest: "", message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`DatanexAI Inquiry: ${formData.interest || "General"}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nOrganization: ${formData.org}\nEmail: ${formData.email}\nRegion: ${formData.region}\nInterest: ${formData.interest}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:info@datanex.ai?subject=${subject}&body=${body}`;
  };

  const inputStyle = {
    width: "100%",
    border: `1px solid ${BRAND.line}`,
    background: BRAND.white,
    color: BRAND.text,
    borderRadius: 14,
    padding: "13px 14px",
    outline: "none",
  };

  return (
    <Section id="contact">
      <div className="dnx-grid-two" style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 28, alignItems: "start" }}>
        <Glass className="dnx-card-hover" style={{ padding: 28 }}>
          <Kicker>{t.nav.contact}</Kicker>
          <h2 style={{ marginTop: 12, fontSize: 36, lineHeight: 1.1, color: BRAND.text, letterSpacing: -0.4 }}>
            {t.contact.title}
          </h2>
          <p style={{ marginTop: 14, color: BRAND.muted, lineHeight: 1.85, fontSize: 16 }}>
            {t.contact.intro}
          </p>
          <p style={{ marginTop: 18, color: BRAND.muted, lineHeight: 1.7, fontSize: 13.5, opacity: 0.85 }}>
            {t.contact.note}
          </p>
        </Glass>

        <Glass className="dnx-card-hover" style={{ padding: 28 }}>
          <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <label style={{ display: "grid", gap: 6, color: BRAND.muted, fontSize: 13, fontWeight: 700 }}>
                {t.contact.form.name}
                <input className="dnx-input" name="name" value={formData.name} onChange={handleChange} required style={inputStyle} />
              </label>
              <label style={{ display: "grid", gap: 6, color: BRAND.muted, fontSize: 13, fontWeight: 700 }}>
                {t.contact.form.org}
                <input className="dnx-input" name="org" value={formData.org} onChange={handleChange} required style={inputStyle} />
              </label>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <label style={{ display: "grid", gap: 6, color: BRAND.muted, fontSize: 13, fontWeight: 700 }}>
                {t.contact.form.email}
                <input className="dnx-input" type="email" name="email" value={formData.email} onChange={handleChange} required style={inputStyle} />
              </label>
              <label style={{ display: "grid", gap: 6, color: BRAND.muted, fontSize: 13, fontWeight: 700 }}>
                {t.contact.form.region}
                <input className="dnx-input" name="region" value={formData.region} onChange={handleChange} style={inputStyle} />
              </label>
            </div>

            <label style={{ display: "grid", gap: 6, color: BRAND.muted, fontSize: 13, fontWeight: 700 }}>
              {t.contact.form.interest}
              <select name="interest" value={formData.interest} onChange={handleChange} required style={inputStyle}>
                <option value="" style={{ color: "#000" }}>{t.contact.form.select}</option>
                {t.contact.form.options.map((option) => (
                  <option key={option} value={option} style={{ color: "#000" }}>{option}</option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6, color: BRAND.muted, fontSize: 13, fontWeight: 700 }}>
              {t.contact.form.message}
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                style={{ ...inputStyle, minHeight: 140, resize: "vertical" }}
              />
            </label>

            <button
              type="submit"
              style={{
                marginTop: 6,
                padding: "16px 22px",
                borderRadius: 16,
                background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.cyan})`,
                color: BRAND.white,
                fontWeight: 800,
                border: 0,
                cursor: "pointer",
                boxShadow: "0 16px 32px rgba(29,78,216,0.22)",
              }}
            >
              {t.contact.form.submit}
            </button>
          </form>
        </Glass>
      </div>
    </Section>
  );
}

function Footer({ t }) {
  return (
    <footer style={{ borderTop: `1px solid ${BRAND.line}`, marginTop: 20 }}>
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "28px 20px",
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          justifyContent: "space-between",
          alignItems: "center",
          color: BRAND.muted,
          fontSize: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img src={IMAGES.datanex} alt="DatanexAI" style={{ height: 32, objectFit: "contain" }} />
          <span style={{ color: BRAND.text, fontWeight: 700 }}>{t.footer.left}</span>
        </div>
        <div style={{ maxWidth: 520, textAlign: "right" }}>{t.footer.right}</div>
      </div>
    </footer>
  );
}

export default function App() {
  usePageStyles();
  const [lang, setLang] = useState("en");
  const [menuOpen, setMenuOpen] = useState(false);
  const t = useMemo(() => CONTENT[lang], [lang]);

  const mobileLinks = [
    [t.nav.speed, "speed"],
    [t.nav.proof, "proof"],
    [t.nav.problem, "problem"],
    [t.nav.solution, "solution"],
    [t.nav.products, "products"],
    [t.nav.flow, "flow"],
    [t.nav.impact, "impact"],
    [t.nav.cases, "cases"],
    [t.nav.licensing, "licensing"],
  ];

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <div className="dnx-app">
      <Header t={t} lang={lang} setLang={setLang} onOpenMenu={() => setMenuOpen(true)} />
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        links={mobileLinks}
        lang={lang}
        setLang={setLang}
        t={t}
      />
      <main>
        <Hero t={t} />
        <SpeedSection t={t} />
        <ProofSection t={t} />
        <ProblemSection t={t} />
        <SolutionSection t={t} />
        <ProductsSection t={t} />
        <FlowSection t={t} />
        <ImpactSection t={t} />
        <CasesSection t={t} />
        <LicensingSection t={t} />
        <ContactSection t={t} />
      </main>
      <Footer t={t} />
    </div>
  );
}
